import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';

/**
 * JM-EX7: prominent "How to pay" card on order detail.
 *
 * Renders the admin-authored `instructions` field (Markdown) inside a Card
 * with hand-styled prose rules. We don't pull in `@tailwindcss/typography`
 * because the supported set of elements is small (headings, ordered/unordered
 * lists, strong, em, links, code, blockquote, hr) — explicit classes are
 * easier to keep on-brand than `prose-*` overrides.
 */
export function PaymentInstructions({
  methodLabel,
  markdown,
  accountLabel,
}: {
  methodLabel: string;
  markdown: string;
  accountLabel?: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">How to pay with {methodLabel}</CardTitle>
        {accountLabel ? (
          <p className="mt-1 text-sm text-muted-foreground">Send to: {accountLabel}</p>
        ) : null}
      </CardHeader>
      <CardContent className="text-sm leading-relaxed">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ node, ...props }) => (
              <h2 className="mt-4 text-base font-semibold first:mt-0" {...props} />
            ),
            h2: ({ node, ...props }) => (
              <h3 className="mt-4 text-sm font-semibold first:mt-0" {...props} />
            ),
            h3: ({ node, ...props }) => (
              <h4 className="mt-3 text-sm font-semibold first:mt-0" {...props} />
            ),
            p: ({ node, ...props }) => <p className="my-2 first:mt-0 last:mb-0" {...props} />,
            ol: ({ node, ...props }) => (
              <ol className="my-2 list-decimal space-y-1 pl-6 marker:text-muted-foreground" {...props} />
            ),
            ul: ({ node, ...props }) => (
              <ul className="my-2 list-disc space-y-1 pl-6 marker:text-muted-foreground" {...props} />
            ),
            li: ({ node, ...props }) => <li className="pl-1" {...props} />,
            strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
            em: ({ node, ...props }) => <em className="italic" {...props} />,
            code: ({ node, ...props }) => (
              <code
                className="rounded bg-muted px-1 py-0.5 font-mono text-xs"
                {...props}
              />
            ),
            a: ({ node, ...props }) => (
              <a
                className="text-primary underline underline-offset-2 hover:no-underline"
                target="_blank"
                rel="noreferrer noopener"
                {...props}
              />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote
                className="my-2 border-l-2 border-muted-foreground/30 pl-3 text-muted-foreground"
                {...props}
              />
            ),
            hr: () => <hr className="my-3 border-border" />,
          }}
        >
          {markdown}
        </ReactMarkdown>
      </CardContent>
    </Card>
  );
}
